import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import Icon from '../../components/AppIcon';

const API_URL = import.meta.env.VITE_API_URL || '';

const CheckinPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { t } = useTranslation();

  const [guest, setGuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState(false);
  const [marked, setMarked] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('checkin.noToken'));
      setLoading(false);
      return;
    }

    const fetchGuest = async () => {
      try {
        const res = await fetch(`${API_URL}/api/checkin/${token}`);
        const data = await res.json();

        if (!res.ok) {
          setError(data.error || t('checkin.invalidToken'));
          return;
        }

        setGuest(data.guest);
        if (data.guest.attended) {
          setMarked(true);
        }
      } catch (err) {
        setError(t('checkin.failedLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchGuest();
  }, [token]);

  const handleCheckin = async () => {
    setMarking(true);
    try {
      const res = await fetch(`${API_URL}/api/checkin/${token}`, { method: 'POST' });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || t('checkin.failedMarkShort'));
        return;
      }

      setGuest(data.guest);
      setMarked(true);
    } catch (err) {
      setError(t('checkin.failedMark'));
    } finally {
      setMarking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-pink-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">{t('checkin.lookingUp')}</p>
        </div>
      </div>
    );
  }

  if (error && !guest) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-sm w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('checkin.invalidCheckin')}</h2>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-sm w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-400 to-rose-400 p-6 text-center text-white">
          <Icon name="QrCode" size={36} className="mx-auto mb-2 opacity-80" />
          <h1 className="text-xl font-bold">{t('checkin.title')}</h1>
        </div>

        <div className="p-6">
          {/* Guest Info */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl font-bold text-pink-500">
                {guest?.guest_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
              </span>
            </div>
            <h2 className="text-xl font-semibold text-gray-800">{guest?.guest_name}</h2>
            <div className="flex items-center justify-center gap-2 mt-2">
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                guest?.status === 'confirmed' ? 'bg-emerald-50 text-emerald-600' :
                guest?.status === 'declined' ? 'bg-red-50 text-red-600' :
                'bg-amber-50 text-amber-600'
              }`}>
                {guest?.status === 'confirmed' ? t('checkin.confirmed') :
                 guest?.status === 'declined' ? t('checkin.declined') : t('checkin.pending')}
              </span>
              {guest?.category && guest.category !== 'Guest' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600">
                  {guest.category}
                </span>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('checkin.partySize')}</span>
              <span className="font-semibold text-gray-800">{guest?.number_of_guests || 1} {(guest?.number_of_guests || 1) > 1 ? t('rsvp.guests') : t('rsvp.guest')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('checkin.ceremony')}</span>
              <span className="font-semibold text-gray-800">{guest?.attending_ceremony ? t('checkin.yes') : t('checkin.no')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('checkin.reception')}</span>
              <span className="font-semibold text-gray-800">{guest?.attending_reception ? t('checkin.yes') : t('checkin.no')}</span>
            </div>
          </div>

          {/* Check-in Button / Status */}
          {marked ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <Icon name="CheckCircle" size={36} className="text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-emerald-600">{t('checkin.checkedIn')}</h3>
              <p className="text-gray-500 text-sm mt-1">{t('checkin.welcomeCelebration')}</p>
            </div>
          ) : (
            <button
              onClick={handleCheckin}
              disabled={marking || guest?.status === 'declined'}
              className="w-full py-3 px-6 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-rose-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              {marking ? t('checkin.marking') : t('checkin.markArrived')}
            </button>
          )}

          {error && guest && (
            <p className="text-red-500 text-sm text-center mt-3">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckinPage;
